// One consignment in a list (Today, Log, an order's history): time, vendor, PO + consignment no.,
// challan, and its receiving status — with Remind right on the card while it waits.

import { useState } from "react";
import { BellRing, CheckCircle2, ChevronRight, FileText, Hourglass, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gateApi, type Consignment } from "@/lib/gate";
import { useGate } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/** "45 min", "2 h 10 min", "3 days". */
export function waited(minutes?: number | null): string {
  const m = Math.max(0, Math.round(minutes ?? 0));
  if (m < 60) return `${m} min`;
  if (m < 24 * 60) return `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}`;
  const d = Math.floor(m / (24 * 60));
  return `${d} day${d === 1 ? "" : "s"}`;
}

export function StatusChip({ c, className }: { c: Consignment; className?: string }) {
  if (c.status === "WAITING")
    return (
      <span
        className={cn(
          "inline-flex w-fit items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200",
          className,
        )}
      >
        <Hourglass className="size-3.5" /> Waiting {waited(c.waitingMinutes)}
      </span>
    );
  if (c.status === "RECEIVED")
    return (
      <span
        className={cn(
          "inline-flex w-fit items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
          className,
        )}
      >
        <CheckCircle2 className="size-3.5" /> Received{c.receivedGrn ? ` · ${c.receivedGrn}` : ""}
      </span>
    );
  return (
    <span
      className={cn("inline-flex w-fit rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground", className)}
    >
      Recorded
    </span>
  );
}

export function challanLabel(c: Consignment): string {
  if (c.challanNumber) return `Challan ${c.challanNumber}`;
  if (c.photoIds.length) return "Challan photo";
  if (c.noChallanReason) return "No challan";
  return "Challan —";
}

export function ConsignmentCard({
  c,
  onOpen,
  onChanged,
  showOrder = true,
}: {
  c: Consignment;
  onOpen: () => void;
  onChanged?: () => void;
  /** False inside an order's own history (vendor / PO are already on screen). */
  showOrder?: boolean;
}) {
  const { token } = useAuth();
  const { gate } = useGate();
  const [reminding, setReminding] = useState(false);

  async function remind() {
    setReminding(true);
    try {
      const r = await gateApi.remind(token, gate.id, c.id);
      if (r?.errorMessage) toast.error(r.errorMessage);
      else {
        toast.success(r?.myMessage ?? "Reminder sent.");
        onChanged?.();
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Could not send the reminder.");
    } finally {
      setReminding(false);
    }
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-2xl border bg-card p-3 shadow-xs",
        c.status === "WAITING" ? "border-amber-300 dark:border-amber-800" : "border-border",
      )}
    >
      <Button
        variant="ghost"
        onClick={onOpen}
        className="-m-1 h-auto items-start gap-3 rounded-xl p-1 text-left font-normal whitespace-normal hover:bg-muted/60"
      >
        <span className="flex w-14 shrink-0 flex-col items-center rounded-xl bg-primary/10 py-1.5 text-primary">
          <span className="text-base font-bold tabular-nums">{c.arrivedTime ?? "—"}</span>
          {c.consignmentNumber != null && (
            <span className="text-[10px] font-semibold uppercase">#{c.consignmentNumber}</span>
          )}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {showOrder && <span className="text-base leading-snug font-semibold">{c.vendor ?? "—"}</span>}
          <span className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            {showOrder && <span className="font-mono text-foreground">{c.poNumber}</span>}
            <span>Consignment {c.consignmentNumber ?? "?"}</span>
            {showOrder && c.store && <span>· {c.store}</span>}
          </span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
            <span
              className={cn("flex items-center gap-1", !c.challanNumber && !c.photoIds.length && "text-destructive")}
            >
              <FileText className="size-3.5" />
              {challanLabel(c)}
            </span>
            {c.photoIds.length > 0 && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <ImageIcon className="size-3.5" /> {c.photoIds.length}
              </span>
            )}
            {c.vehicleNumber && <span className="font-mono text-muted-foreground">{c.vehicleNumber}</span>}
          </span>
          <StatusChip c={c} />
        </span>
        <ChevronRight className="size-5 self-center text-muted-foreground" />
      </Button>
      {c.status === "WAITING" && (
        <Button variant="outline" className="h-10 rounded-xl" disabled={reminding} onClick={() => void remind()}>
          {reminding ? <Loader2 className="animate-spin" /> : <BellRing />} Remind receiver
          {c.remindCount ? <span className="text-xs text-muted-foreground">({c.remindCount} sent)</span> : null}
        </Button>
      )}
    </div>
  );
}
